import type { AxiosResponse } from "axios"
import type {
	TAutoConnectEcallElevationResponse,
	TAutoConnectErefEcallsResponse,
	TDisconnectedEref,
	TEcall,
	TElevationNumber,
	TEref,
	TFurniture,
	TFurnitureOrdering,
	TPageElevationData,
} from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"

/**
 * Walks each page in the cached quotation, finds an entity by id inside the
 * named array (`erefs` or `ecalls`), and replaces it via `updater`. Returns the
 * same `cached` reference when no entity matched, so TanStack's `setQueryData`
 * doesn't notify subscribers on no-op writes.
 */
const mapEntityInPages = <K extends "erefs" | "ecalls">(
	cached: AxiosResponse<TQuotationDetail> | undefined,
	field: K,
	id: number,
	updater: (entity: TPageElevationData[K][number]) => TPageElevationData[K][number] | undefined,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached
	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		const list = page[field] as TPageElevationData[K]
		const idx = list.findIndex((entry) => entry.id === id)
		if (idx === -1) return page
		const nextEntry = updater(list[idx])
		if (!nextEntry) return page
		changed = true
		const nextList = list.slice() as TPageElevationData[K]
		nextList[idx] = nextEntry as TPageElevationData[K][number]
		return { ...page, [field]: nextList }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

export const spliceErefIntoCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	updated: TEref,
): AxiosResponse<TQuotationDetail> | undefined =>
	mapEntityInPages(cached, "erefs", updated.id, (existing) => {
		// Deep-merge estimation_config so locally-known fields (e.g. an in-flight
		// or just-persisted delivery_date) aren't wiped out when a non-config
		// PATCH (status, elevation_number, comment) returns a TEref whose
		// estimation_config is missing or partially populated.
		const mergedConfig =
			existing.estimation_config || updated.estimation_config
				? ({ ...existing.estimation_config, ...updated.estimation_config } as TEref["estimation_config"])
				: undefined
		return {
			...existing,
			...updated,
			...(mergedConfig ? { estimation_config: mergedConfig } : {}),
		}
	})

export const spliceEcallIntoCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	updated: TEcall,
): AxiosResponse<TQuotationDetail> | undefined =>
	mapEntityInPages(cached, "ecalls", updated.id, (existing) => ({ ...existing, ...updated }))

export const appendEcallElevationToCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	ecallId: number,
	nextEcallVersion: number,
	created: TElevationNumber,
): AxiosResponse<TQuotationDetail> | undefined =>
	mapEntityInPages(cached, "ecalls", ecallId, (existing) => ({
		...existing,
		version: nextEcallVersion,
		elevation_numbers: [...existing.elevation_numbers, created],
	}))

export const replaceEcallElevationInCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	ecallId: number,
	updated: TElevationNumber,
	nextEcallVersion?: number,
): AxiosResponse<TQuotationDetail> | undefined =>
	mapEntityInPages(cached, "ecalls", ecallId, (existing) => {
		const idx = existing.elevation_numbers.findIndex((en) => en.id === updated.id)
		if (idx === -1) return existing
		const nextElevations = existing.elevation_numbers.slice()
		nextElevations[idx] = { ...nextElevations[idx], ...updated }
		return {
			...existing,
			version: nextEcallVersion ?? existing.version,
			elevation_numbers: nextElevations,
		}
	})

export const replaceEcallElevationsInCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	ecallId: number,
	updates: TElevationNumber[],
	nextEcallVersion?: number,
): AxiosResponse<TQuotationDetail> | undefined =>
	mapEntityInPages(cached, "ecalls", ecallId, (existing) => {
		const byId = new Map(updates.map((u) => [u.id, u]))
		const nextElevations = existing.elevation_numbers.map((en) => {
			const patch = byId.get(en.id)
			return patch ? { ...en, ...patch } : en
		})
		return {
			...existing,
			version: nextEcallVersion ?? existing.version,
			elevation_numbers: nextElevations,
		}
	})

export const upsertEcallElevationsInCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	ecallId: number,
	updates: TElevationNumber[],
	nextEcallVersion?: number,
): AxiosResponse<TQuotationDetail> | undefined =>
	mapEntityInPages(cached, "ecalls", ecallId, (existing) => {
		const byId = new Map(updates.map((u) => [u.id, u]))
		const merged = existing.elevation_numbers.map((en) => {
			const patch = byId.get(en.id)
			if (!patch) return en
			byId.delete(en.id)
			return { ...en, ...patch }
		})
		const appended = Array.from(byId.values())
		return {
			...existing,
			version: nextEcallVersion ?? existing.version,
			elevation_numbers: appended.length > 0 ? [...merged, ...appended] : merged,
		}
	})

export const removeEcallElevationFromCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	ecallId: number,
	elevationId: number,
	nextEcallVersion?: number,
): AxiosResponse<TQuotationDetail> | undefined =>
	mapEntityInPages(cached, "ecalls", ecallId, (existing) => {
		const idx = existing.elevation_numbers.findIndex((en) => en.id === elevationId)
		if (idx === -1) return existing
		return {
			...existing,
			version: nextEcallVersion ?? existing.version,
			elevation_numbers: existing.elevation_numbers.filter((en) => en.id !== elevationId),
		}
	})

export const applyAutoConnectErefIntoCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	response: TAutoConnectErefEcallsResponse,
): { next: AxiosResponse<TQuotationDetail> | undefined; addedElevationIds: number[] } => {
	if (!cached?.data?.related_objects) return { next: cached, addedElevationIds: [] }
	const { eref: nextEref, connected_ecalls } = response

	const beforeIds = new Set<number>()
	cached.data.related_objects.forEach((page) =>
		page.ecalls.forEach((ecall) =>
			ecall.elevation_numbers.forEach((en) => {
				if (en.eref_id === nextEref.id) beforeIds.add(en.id)
			}),
		),
	)

	let next: AxiosResponse<TQuotationDetail> | undefined = cached
	connected_ecalls.forEach((patch) => {
		next = upsertEcallElevationsInCache(next, patch.id, patch.connected_elevations, patch.version)
	})
	next = spliceErefIntoCache(next, nextEref)

	const addedElevationIds: number[] = []
	connected_ecalls.forEach((ecall) =>
		ecall.connected_elevations.forEach((en) => {
			if (!beforeIds.has(en.id)) addedElevationIds.push(en.id)
		}),
	)

	return { next, addedElevationIds }
}

export const applyAutoConnectEcallElevationIntoCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	response: TAutoConnectEcallElevationResponse,
): AxiosResponse<TQuotationDetail> | undefined => {
	const { ecall_elevation, ecall, eref } = response
	let next = replaceEcallElevationInCache(cached, ecall.id, ecall_elevation, ecall.version)
	next = spliceErefIntoCache(next, eref)
	return next
}

export const incrementErefVersionInCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	erefId: number,
): AxiosResponse<TQuotationDetail> | undefined =>
	mapEntityInPages(cached, "erefs", erefId, (existing) => ({ ...existing, version: existing.version + 1 }))

/**
 * Reconciles erefs that the backend disconnected from `ecallId` while editing
 * its page or one of its elevation numbers. For each returned eref it:
 *  1. advances the cached eref's version to the backend's post-disconnection
 *     value (so the next edit on that eref doesn't fail with a version
 *     conflict). Only moves the version forward — a concurrent in-flight eref
 *     mutation may already have advanced the cache past this (possibly stale)
 *     response, and regressing it would itself cause a conflict.
 *  2. clears the connection by nulling `eref_id` on any of that ecall's
 *     elevations that still point at the disconnected eref.
 */
export const applyDisconnectedErefsToCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	ecallId: number,
	disconnectedErefs: TDisconnectedEref[] | undefined,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!disconnectedErefs?.length) return cached
	const versionByErefId = new Map(disconnectedErefs.map((eref) => [eref.id, eref.version]))

	let next = cached
	disconnectedErefs.forEach((eref) => {
		next = mapEntityInPages(next, "erefs", eref.id, (existing) =>
			existing.version >= eref.version ? existing : { ...existing, version: eref.version },
		)
	})

	return mapEntityInPages(next, "ecalls", ecallId, (ecall) => {
		let changed = false
		const elevation_numbers = ecall.elevation_numbers.map((en) => {
			if (en.eref_id !== null && versionByErefId.has(en.eref_id)) {
				changed = true
				return { ...en, eref_id: null }
			}
			return en
		})
		return changed ? { ...ecall, elevation_numbers } : ecall
	})
}

/**
 * Reconciles ecalls / ecall_elevations that the backend disconnected from an
 * eref while editing that eref (e.g. its elevation number). Each returned
 * ecall is the full post-disconnection row, so merging it replaces the cached
 * `version` and `elevation_numbers` in one shot — severing the connection
 * (`eref_id` is already nulled in the payload) and syncing versions.
 *
 * The version is only moved forward (`existing.version >= updated.version`
 * is left untouched): a concurrent in-flight ecall mutation may already have
 * advanced the cache past this (possibly stale/out-of-order) response, and
 * regressing it would itself cause a conflict — same invariant as
 * `applyDisconnectedErefsToCache`.
 *
 * `disconnectedEcallElevations` is a flat fallback for elevations whose parent
 * ecall wasn't returned in `disconnectedEcalls`; elevations belonging to an
 * already-merged ecall are skipped so they can't clobber the version guard.
 */
export const applyDisconnectedEcallsToCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	disconnectedEcalls: TEcall[] | undefined,
	disconnectedEcallElevations: TElevationNumber[] | undefined,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!disconnectedEcalls?.length && !disconnectedEcallElevations?.length) return cached

	let next = cached
	const mergedEcallIds = new Set<number>()
	;(disconnectedEcalls ?? []).forEach((ecall) => {
		mergedEcallIds.add(ecall.id)
		next = mapEntityInPages(next, "ecalls", ecall.id, (existing) =>
			existing.version >= ecall.version ? existing : { ...existing, ...ecall },
		)
	})
	;(disconnectedEcallElevations ?? []).forEach((elevation) => {
		if (elevation.ecall_id == null || mergedEcallIds.has(elevation.ecall_id)) return
		next = replaceEcallElevationInCache(next, elevation.ecall_id, elevation)
	})
	return next
}

/**
 * Same pattern as `spliceErefIntoCache`, but for furniture inside
 * `related_objects[].furniture`. Used to apply mutation responses
 * (status, dimensions, config, etc.) directly to the cache without a refetch.
 */
export const spliceFurnitureIntoCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	updated: TFurniture,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached
	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		const idx = page.furniture.findIndex((f) => f.id === updated.id)
		if (idx === -1) return page
		changed = true
		const nextFurniture = page.furniture.slice()
		// Merge (don't replace) so fields the PATCH response omits — notably
		// `eref_id` — fall back to the cached value instead of becoming undefined.
		// An undefined eref_id would make the furniture vanish from the sidebars,
		// which group strictly by `eref_id === null` vs. a real eref id.
		// Mirrors `spliceErefIntoCache`.
		nextFurniture[idx] = { ...page.furniture[idx], ...updated }
		return { ...page, furniture: nextFurniture }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

/**
 * Inserts a brand-new furniture (e.g. the result of the duplicate endpoint) into the
 * cached quotation. Unlike `spliceFurnitureIntoCache` (update-only — it no-ops when the id
 * isn't already present), this appends the furniture to the `furniture[]` of the page that
 * matches its `page_number`, so the new highlight/sidebar entry appears without a refetch.
 *
 * Defensive guards: a furniture whose id already exists is merged in place rather than
 * duplicated (so a double-call can't add it twice), and a `page_number` with no matching
 * page leaves the cache untouched.
 */
export const insertFurnitureIntoCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	created: TFurniture,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached

	// If the id already exists anywhere, fall back to an in-place merge (idempotent).
	const exists = cached.data.related_objects.some((page) => page.furniture.some((f) => f.id === created.id))
	if (exists) return spliceFurnitureIntoCache(cached, created)

	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		if (page.page_number !== created.page_number) return page
		changed = true
		return { ...page, furniture: [...page.furniture, created] }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

/**
 * Re-orders the furniture of a single group inside the cached quotation to match
 * the backend-provided `ordering` from a furniture PATCH response. The group is
 * either the furnitures connected to `parent_eref_id`, or — when that is `null` —
 * the no-eref furnitures on `page_number`.
 *
 * `related_objects[].furniture` is a flat per-page list; the left sidebar groups
 * it by `eref_id` and the eref sidebar filters by `eref_id`, both preserving this
 * array's order. Re-ordering the group's items into their existing slots therefore
 * fixes both views at once while leaving other groups untouched.
 */
export const reorderFurnitureInCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	ordering: TFurnitureOrdering,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached
	const { page_number, parent_eref_id, furnitures_ids_in_order } = ordering

	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		if (page.page_number !== page_number) return page

		const belongsToGroup = (f: TFurniture) =>
			parent_eref_id === null ? f.eref_id === null : f.eref_id === parent_eref_id

		const groupById = new Map<number, TFurniture>()
		for (const f of page.furniture) {
			if (belongsToGroup(f)) groupById.set(f.id, f)
		}
		if (groupById.size === 0) return page

		// Build the group in the backend order, then append any group members the
		// ordering omitted (defensive) so no furniture is dropped from the cache.
		const orderedIds = new Set(furnitures_ids_in_order)
		const orderedGroup: TFurniture[] = []
		for (const id of furnitures_ids_in_order) {
			const f = groupById.get(id)
			if (f) orderedGroup.push(f)
		}
		for (const f of page.furniture) {
			if (belongsToGroup(f) && !orderedIds.has(f.id)) orderedGroup.push(f)
		}

		// Refill the group's existing slots in the flat list; other furniture stays put.
		let gi = 0
		let pageChanged = false
		const nextFurniture = page.furniture.map((f) => {
			if (!belongsToGroup(f)) return f
			const replacement = orderedGroup[gi++]
			if (replacement !== f) pageChanged = true
			return replacement
		})
		if (!pageChanged) return page

		changed = true
		return { ...page, furniture: nextFurniture }
	})

	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}
